/**
 * =============================================================================
 * useFormResumeBootstrap — production resume orchestration for Intake V2
 * =============================================================================
 *
 * Responsibilities
 * ----------------
 * 1. Detect `?formUid=` / `?formid=` (any casing) or sessionStorage resume id
 * 2. Fetch existing form from Jema API
 * 3. Hydrate IntakeData (API wins over URL prefill; never localStorage cache)
 * 4. Jump user to first incomplete card
 * 5. Show barrier when form is locked (submitted, verified, finalized, or has submittedAt)
 *
 * Explicit non-responsibilities (handled elsewhere — do not duplicate)
 * ---------------------------------------------------------------------
 * - `?token=` trusted magic-link bootstrap → IntakeV2Page trusted effect
 * - `?verified=1&formUid=` email verified redirect → IntakeV2Page verified effect
 * - `?ref=` referral capture → referralTracker on mount
 * - Draft PATCH autosync → performSync / ensureDraft in IntakeV2Page
 *
 * Email change policy
 * -------------------
 * After resume, `draftEmailRef` is set to the server email so `ensureDraft` will
 * NOT POST a new form until the patient changes email on the contact card.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/macro";
import type { IntakeData } from "@/lib/types";
import type { Specialty } from "@/lib/patientIntakeApi";
import {
  buildFormPayload,
  patientIntakeApi,
} from "@/lib/patientIntakeApi";
import {
  buildResumeIntakeState,
  classifyFormResume,
  clearResumeFormUidFromUrl,
  ENABLE_FORM_RESUME,
  getPathLocale,
  getResumeFormUidFromSearch,
  getStoredResumeFormUid,
  mergeResumeFormMedia,
  normalizeResumeFormUid,
  setStoredResumeFormUid,
  stripFreshIntakeSearchParams,
  upgradeProcedureIfNeeded,
} from "@/lib/intakeFormResume";
import { clearStoredReferralSession } from "@/lib/referralTracker";
import { resolveResumeCardIndex } from "@/intake-v2/cardValidation";
import type { CardDef } from "@/intake-v2/types";
import { i18n as linguiCatalog } from "@/i18n";

export type FormResumeStatus =
  | "idle"
  | "loading"
  | "ready"
  | "submitted"
  | "error";

type UseFormResumeBootstrapOptions = {
  searchParams: URLSearchParams;
  setSearchParams: (
    next:
      | URLSearchParams
      | ((prev: URLSearchParams) => URLSearchParams),
    opts?: { replace?: boolean },
  ) => void;
  trustedToken: string | null;
  verifiedParam: string | null;
  queryFormUid: string | null;
  trustedStatus: "idle" | "loading" | "ready" | "error";
  specialties: Specialty[];
  initialUrlPrefill: Partial<IntakeData>;
  baseIntakeData: IntakeData;
  cards: CardDef[];
  i18n: I18n;
  intakeRef: MutableRefObject<IntakeData>;
  updateIntake: (updates: Partial<IntakeData>) => void;
  jumpToCard: (cardId: string) => void;
  clearCachedIntakeData: () => void;
  persistToCache: (data: IntakeData) => void;
  cacheWriteEnabledRef: MutableRefObject<boolean>;
  draftEmailRef: MutableRefObject<string | null>;
  lastSyncSigRef: MutableRefObject<string | null>;
  syncGenRef: MutableRefObject<number>;
  normalizeEmail: (email?: string) => string;
};

export function useFormResumeBootstrap({
  searchParams,
  setSearchParams,
  trustedToken,
  verifiedParam,
  queryFormUid,
  trustedStatus,
  specialties,
  initialUrlPrefill,
  baseIntakeData,
  cards,
  i18n,
  intakeRef,
  updateIntake,
  jumpToCard,
  clearCachedIntakeData,
  persistToCache,
  cacheWriteEnabledRef,
  draftEmailRef,
  lastSyncSigRef,
  syncGenRef,
  normalizeEmail,
}: UseFormResumeBootstrapOptions) {
  const [resumeStatus, setResumeStatus] = useState<FormResumeStatus>(() => {
    if (!ENABLE_FORM_RESUME || typeof window === "undefined") return "idle";
    try {
      if (new URLSearchParams(window.location.search).get("token")?.trim()) {
        return "idle";
      }
      const uid =
        getResumeFormUidFromSearch(searchParams) ?? getStoredResumeFormUid();
      return uid ? "loading" : "idle";
    } catch {
      return "idle";
    }
  });

  const [resumeError, setResumeError] = useState<string | null>(null);
  const [submittedBarrierData, setSubmittedBarrierData] =
    useState<Partial<IntakeData> | null>(null);

  /** Ensures bootstrap fetch runs only once per mount / new-intake reset. */
  const resumeBootRef = useRef(false);
  /** Set after hydrate; consumed by jump effect once card flow is ready. */
  const pendingResumeJumpRef = useRef(false);
  /** Server email at hydrate time — used to detect email change → new form. */
  const resumeSourceEmailRef = useRef<string | null>(null);

  // ── Step 1: Fetch form from API when formUid is in URL or session ─────────
  useEffect(() => {
    if (!ENABLE_FORM_RESUME || resumeBootRef.current) return;
    if (trustedToken) return;
    if (verifiedParam === "1" && queryFormUid) return;

    const rawUid =
      getResumeFormUidFromSearch(searchParams) ?? getStoredResumeFormUid();
    const formUid = normalizeResumeFormUid(rawUid);
    if (!formUid) {
      if (resumeStatus === "loading") setResumeStatus("idle");
      return;
    }

    resumeBootRef.current = true;
    let cancelled = false;

    (async () => {
      setResumeStatus("loading");
      setResumeError(null);
      console.info("[FormResume] Loading form:", formUid);

      try {
        const response = await patientIntakeApi.getFormByUid(formUid);
        if (cancelled) return;

        if (!response?.data?.formUid) {
          throw new Error(linguiCatalog._(msg`Form not found.`));
        }

        const form = response.data;

        let formForResume = form;
        try {
          const fullMedia = await patientIntakeApi.getFormMedia(formUid);
          formForResume = mergeResumeFormMedia(
            form,
            fullMedia as Array<Record<string, unknown>>,
          );
        } catch (mediaErr) {
          console.warn(
            "[FormResume] Could not load full media list; using embedded media",
            mediaErr,
          );
        }

        const classified = classifyFormResume(formForResume, {
          specialties,
          urlPrefill: initialUrlPrefill,
        });

        if (classified.kind === "submitted") {
          const identity = form.identity ?? {};
          setSubmittedBarrierData({
            firstName:
              typeof identity.firstName === "string"
                ? identity.firstName
                : undefined,
            lastName:
              typeof identity.lastName === "string"
                ? identity.lastName
                : undefined,
            email:
              typeof identity.email === "string"
                ? identity.email.toLowerCase()
                : form.patientEmail?.toLowerCase(),
            referenceUid: form.referenceUid,
            formUid: normalizeResumeFormUid(form.formUid) ?? form.formUid,
          });
          setStoredResumeFormUid(formUid);
          clearResumeFormUidFromUrl();
          setResumeStatus("submitted");
          return;
        }

        // Drop stale localStorage so it cannot fight API data on next persist tick.
        clearCachedIntakeData();

        const hydrated = buildResumeIntakeState(classified.intake, {
          base: baseIntakeData,
          urlPrefill: initialUrlPrefill,
          pathLocale: getPathLocale(),
        });

        updateIntake(hydrated);

        const normalizedEmail = normalizeEmail(intakeRef.current.email);
        draftEmailRef.current = normalizedEmail || null;
        resumeSourceEmailRef.current = normalizedEmail || null;
        lastSyncSigRef.current = JSON.stringify(
          buildFormPayload(intakeRef.current),
        );

        setStoredResumeFormUid(formUid);
        clearResumeFormUidFromUrl();
        pendingResumeJumpRef.current = true;
        setResumeStatus("ready");
        console.info("[FormResume] Hydrated draft form:", formUid);
      } catch (err) {
        if (cancelled) return;
        resumeBootRef.current = false;
        console.error("[FormResume] Failed to load form:", err);
        setResumeStatus("error");
        setResumeError(
          err instanceof Error
            ? err.message
            : linguiCatalog._(msg`Could not load your saved form.`),
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    searchParams,
    trustedToken,
    verifiedParam,
    queryFormUid,
    specialties,
    initialUrlPrefill,
    updateIntake,
    baseIntakeData,
    clearCachedIntakeData,
    draftEmailRef,
    lastSyncSigRef,
    intakeRef,
    normalizeEmail,
  ]);

  // ── Step 2: Jump to first incomplete card after card flow rebuilds ─────────
  useEffect(() => {
    if (!pendingResumeJumpRef.current || resumeStatus !== "ready") return;
    if (cards.length === 0) return;
    if (trustedToken && trustedStatus !== "ready" && trustedStatus !== "error") {
      return;
    }

    const idx = resolveResumeCardIndex(cards, intakeRef.current, i18n);
    const target = cards[idx];
    if (target) {
      jumpToCard(target.id);
      console.info("[FormResume] Jumped to card:", target.id);
    }
    pendingResumeJumpRef.current = false;
  }, [cards, resumeStatus, jumpToCard, i18n, trustedToken, trustedStatus, intakeRef]);

  // ── Step 3: Upgrade procedure slug → object when specialties finish loading ─
  useEffect(() => {
    if (resumeStatus !== "ready" && resumeStatus !== "idle") return;
    if (!intakeRef.current.formUid) return;
    const upgraded = upgradeProcedureIfNeeded(
      intakeRef.current.procedures,
      specialties,
    );
    if (upgraded) {
      updateIntake({ procedures: upgraded });
    }
  }, [specialties, resumeStatus, updateIntake, intakeRef]);

  /** Clears resume session and restarts a fresh intake (barrier CTA or error fallback). */
  const handleStartNewIntake = useCallback(() => {
    setStoredResumeFormUid(null);
    resumeSourceEmailRef.current = null;
    pendingResumeJumpRef.current = false;
    resumeBootRef.current = false;
    setSubmittedBarrierData(null);
    setResumeError(null);
    setResumeStatus("idle");
    cacheWriteEnabledRef.current = true;
    clearCachedIntakeData();
    clearStoredReferralSession();
    draftEmailRef.current = null;
    lastSyncSigRef.current = null;
    syncGenRef.current += 1;

    const locale =
      intakeRef.current.preferredLanguage === "es" ? "es" : "en";
    const reset: IntakeData = {
      ...baseIntakeData,
      preferredLanguage: locale,
    };
    intakeRef.current = reset;
    updateIntake(reset);
    persistToCache(reset);

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        stripFreshIntakeSearchParams(next);
        return next;
      },
      { replace: true },
    );

    jumpToCard("preferredLanguage");
  }, [
    jumpToCard,
    persistToCache,
    setSearchParams,
    baseIntakeData,
    updateIntake,
    clearCachedIntakeData,
    cacheWriteEnabledRef,
    draftEmailRef,
    lastSyncSigRef,
    syncGenRef,
    intakeRef,
  ]);

  /** Call from ensureDraft when email changes — breaks link to resumed formUid. */
  const clearResumeSession = useCallback(() => {
    setStoredResumeFormUid(null);
    resumeSourceEmailRef.current = null;
  }, []);

  return {
    resumeStatus,
    resumeError,
    submittedBarrierData,
    handleStartNewIntake,
    clearResumeSession,
    resumeSourceEmailRef,
    isResumeBootstrapPending: resumeStatus === "loading",
  };
}
